import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { JourneyTaskProgressRow } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalJourneyDetail } from "./types";

// POST /api/portal/journeys/progress/:progressId/snooze — flip a task to
// 'snoozed' with snoozed_until + optional snooze_reason. Mirrors the
// 3-key invalidation contract from use-set-progress-status (bucket 12):
//   1. portal.journeys.detail(assignmentId)
//   2. portal.tasks.list()
//   3. portal.tasks.openCount()

type Input = {
  progressId: string;
  assignmentId: string;
  snoozedUntil: string;
  snoozeReason: string | null;
};

type Context = {
  previousDetail: PortalJourneyDetail | undefined;
};

export function useSnoozeTask() {
  const queryClient = useQueryClient();
  return useMutation<
    JourneyTaskProgressRow,
    ApiClientError,
    Input,
    Context
  >({
    mutationFn: ({ progressId, snoozedUntil, snoozeReason }) =>
      apiFetch<JourneyTaskProgressRow>(
        `/portal/journeys/progress/${progressId}/snooze`,
        {
          method: "POST",
          body: JSON.stringify({
            snoozed_until: snoozedUntil,
            snooze_reason: snoozeReason,
          }),
        },
      ),
    onMutate: async ({ progressId, assignmentId, snoozedUntil, snoozeReason }) => {
      const key = queryKeys.portal.journeys.detail(assignmentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail =
        queryClient.getQueryData<PortalJourneyDetail>(key);
      queryClient.setQueryData<PortalJourneyDetail>(key, (old) =>
        old
          ? {
              ...old,
              progress: old.progress.map((p) =>
                p.id === progressId
                  ? {
                      ...p,
                      status: "snoozed",
                      snoozed_until: snoozedUntil,
                      snooze_reason: snoozeReason,
                      completed_at: null,
                    }
                  : p,
              ),
            }
          : old,
      );
      return { previousDetail };
    },
    onError: (err, { assignmentId }, ctx) => {
      if (ctx?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.portal.journeys.detail(assignmentId),
          ctx.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to snooze task");
    },
    onSettled: (_data, _err, { assignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.detail(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portal.tasks.openCount(),
      });
    },
  });
}
